import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
import { AlertHeader, Button, Checkbox, Input, Label, ModalContent, ModalFactory, ModalFooter, Text, TooltipIcon, TooltipWrapper } from 'components';
import { appConfig } from 'config/app';
import { lineHeight, theme, toRem } from 'design';
import { ICalculatorPaymentForm } from 'screens/MakePayment/Calculator/types';
import { IPaymentTotals, ISubmitPaymentPayload, ITipCalculatorParticipant } from 'types';
import { centsToDollarsDisplay, formatDateLongWithDay } from 'utils';

interface ISubmitPaymentModalProps {
  values: ISubmitPaymentPayload | ICalculatorPaymentForm;
  totals: IPaymentTotals;
  participants?: ITipCalculatorParticipant[];
  isTipCalculator?: boolean;
  disabled?: boolean;
  isLoading: boolean;
  dataCy?: string;
  setFieldValue: (field: string, value: any) => void;
  onSubmit(): void;
  onClose(): void;
}

const Layout = styled.section`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
`;

const ModalHeader = styled.div`
  display: flex;
  flex-flow: column nowrap;

  h2 {
    font-size: ${toRem(theme.font.size['2xl'])};
    line-height: ${lineHeight(theme.font.size['2xl'])};
  }

  > * {
    padding-bottom: ${toRem(theme.space.lg)};
  }
`;

export const Totals = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
  padding-bottom: ${toRem(theme.space.lg)};
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
`;

export const RowItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.sm)};

  p {
    color: ${theme.color.neutral[500]};
  }

  &.gross {
    p {
      color: ${theme.color.neutral[900]};
    }
  }
`;

const StyledModalContent = styled(ModalContent)`
  padding: ${toRem(theme.space.lg)} 0 ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.xl)};

  a {
    color: ${theme.color.primary[500]};
    text-decoration: none;

    :hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${theme.breakpoint.med}px) {
    span {
      font-size: ${toRem(theme.font.size.sm)};
      line-height: ${lineHeight(theme.font.size.sm)};
    }
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  border-top: 1px solid ${theme.color.neutral[200]};
  padding-top: ${toRem(theme.space['3xl'])};
`;

const StyledButton = styled(Button)`
  padding: 0 ${toRem(theme.space['3xl'])};
`;

const FieldContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
  gap: ${toRem(theme.space.sm)};

  label {
    span {
      color: ${theme.color.neutral[400]};
    }
  }
`;

const descMaxLength = 100;
const authorizedAgreementLabel = (
  <span data-cy="authorized-agreement-text">
    I am authorized to make these transactions and aware of the{' '}
    <a href={appConfig.LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
      Terms and Conditions
    </a>{' '}
    to send payments.
  </span>
);

const SubmitPaymentModal: React.FC<ISubmitPaymentModalProps> = ({ values, totals, participants, setFieldValue, disabled, isLoading, onClose, onSubmit, isTipCalculator = false, dataCy = 'submit-payment-modal' }) => (
  <ModalFactory dataCy={dataCy} onClose={onClose} maxWidth={485} isActive>
    <Layout>
      <ModalHeader>
        <AlertHeader icon="DollarFill" iconFill={theme.color.secondary[500]} size={45} title={`You are about to submit payments${values.paymentDate ? ` for ${formatDateLongWithDay(values.paymentDate)}` : ''} `} />
      </ModalHeader>
      <Totals>
        <RowItem data-cy={`${dataCy}-gross`} className="gross">
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-gross-label`}>
            {isTipCalculator ? 'Calculated Amount (Gross)' : 'Amount Entered (Gross)'}
          </Text>
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-gross-value`}>
            {centsToDollarsDisplay(totals?.gross ?? 0)}
          </Text>
        </RowItem>
        {totals?.cash > 0 && (
          <RowItem data-cy={`${dataCy}-cash`}>
            <Text size="small" data-cy={`${dataCy}-cash-label`}>
              Cash
            </Text>
            <Text size="small" data-cy={`${dataCy}-cash-value`}>
              {centsToDollarsDisplay(totals.cash)}
            </Text>
          </RowItem>
        )}
        <RowItem data-cy={`${dataCy}-payroll`}>
          <Text size="small" data-cy={`${dataCy}-payroll-label`}>
            Payroll
          </Text>
          <Text size="small" data-cy={`${dataCy}-payroll-value`}>
            {centsToDollarsDisplay(totals?.payroll ?? 0)}
          </Text>
        </RowItem>
        <RowItem data-cy={`${dataCy}-instant`}>
          <Text size="small" data-cy={`${dataCy}-instant-label`}>
            Instant
          </Text>
          <Text size="small" data-cy={`${dataCy}-instant-value`}>
            {centsToDollarsDisplay(totals?.instant ?? 0)}
          </Text>
        </RowItem>
        {totals?.holdbacks > 0 && (
          <RowItem data-cy={`${dataCy}-holdbacks`}>
            <TooltipWrapper>
              <Text size="small" data-cy={`${dataCy}-holdbacks-label`}>
                Holdbacks
              </Text>
              <TooltipIcon id={`${dataCy}-holdback-tooltip`}>Amount withheld to cover expenses</TooltipIcon>
            </TooltipWrapper>
            <Text size="small" data-cy={`${dataCy}-holdbacks-value`}>
              -{centsToDollarsDisplay(totals.holdbacks)}
            </Text>
          </RowItem>
        )}
        {participants?.map((participant) => (
          <RowItem key={`participant_${participant.name}`} data-cy={`${dataCy}-participant`}>
            <Text size="small" data-cy={`${dataCy}-participant-label`}>
              {participant.name}
            </Text>
            <Text size="small" data-cy={`${dataCy}-participant-value`}>
              {centsToDollarsDisplay(participant.net)}
            </Text>
          </RowItem>
        ))}
        <RowItem data-cy={`${dataCy}-pay-now`} className="gross">
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-pay-now-label`}>
            Pay Now
          </Text>
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-pay-now-value`}>
            {centsToDollarsDisplay(totals?.payNow ?? 0)}
          </Text>
        </RowItem>
      </Totals>
      <StyledModalContent>
        <FieldContainer>
          <Label>
            Description <span>(Optional)</span>
          </Label>
          <Input data-cy={`${dataCy}-description-input`} defaultValue={values.description} onBlur={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('description', e.target.value)} disabled={isLoading} maxLength={descMaxLength} />
        </FieldContainer>
        <Checkbox
          id="authorizedAgreement"
          name="authorizedAgreement"
          data-cy={`${dataCy}-authorized-agreement-checkbox`}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('authorizedAgreement', e.target.checked)}
          checked={values.authorizedAgreement}
          disabled={isLoading}
          label={authorizedAgreementLabel}
        />
      </StyledModalContent>

      <StyledModalFooter columns={1}>
        <StyledButton label="Submit Payment" data-cy={`${dataCy}-payment-submit-btn`} onClick={onSubmit} isProcessing={isLoading} disabled={disabled} />
      </StyledModalFooter>
    </Layout>
  </ModalFactory>
);

export default SubmitPaymentModal;
